import { toURLSearchParams } from '@wix/sdk-runtime/rest-modules';
import { transformSDKTimestampToRESTTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformSDKFieldMaskToRESTFieldMask } from '@wix/sdk-runtime/transformations/field-mask';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveWixEventsV2PolicyManagementUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    _: [
      {
        srcPath: '/_api/events-policies',
        destPath: '',
      },
    ],
    'api._api_base_domain_': [
      {
        srcPath: '/events-policies',
        destPath: '',
      },
    ],
    'manage._base_domain_': [
      {
        srcPath: '/_api/events-policies',
        destPath: '',
      },
    ],
    'www.wixapis.com': [
      {
        srcPath: '/events-policies',
        destPath: '',
      },
      {
        srcPath: '/events/v2/policies',
        destPath: '/v2/policies',
      },
    ],
    '*.dev.wix-code.com': [
      {
        srcPath: '/_api/events-policies',
        destPath: '',
      },
    ],
    'editor.wixapps.net': [
      {
        srcPath: '/_api/events-policies',
        destPath: '',
      },
    ],
    'www._base_domain_': [
      {
        srcPath: '/_api/events-policies',
        destPath: '',
      },
    ],
    'events.wixapps.net': [
      {
        srcPath: '/_api/events-policies',
        destPath: '',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_events_policies';

/**
 * Creates a policy.
 *
 *
 * <!--
 * >  Note: This function is restricted and only runs if you elevate permissions using the [wix-auth.elevate()](https://www.wix.com/velo/reference/wix-auth/elevate) function.
 * -->
 *
 * The `createPolicy()` function returns a Promise that resolves to the newly-created policy.
 *
 * You can create up to 3 policies per event. If you try to create more than 3, you'll get the "Maximum number of policies for the event has been reached" error.
 */
export function createPolicy(payload: object): RequestOptionsFactory<any> {
  function __createPolicy({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [{ path: 'policy.createdDate' }, { path: 'policy.updatedDate' }],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.events.v2.policy',
      method: 'POST' as any,
      methodFqn: 'wix.events.v2.PolicyManagement.CreatePolicy',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixEventsV2PolicyManagementUrl({
        protoPath: '/v2/policies',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'policy.createdDate' },
              { path: 'policy.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __createPolicy;
}

/**
 * Updates a policy.
 *
 * <!--
 * > Note: This function is restricted and only runs if you elevate permissions using the [wix-auth.elevate()](https://www.wix.com/velo/reference/wix-auth/elevate) function.
 * -->
 *
 * The `updatePolicy()` function returns a Promise that resolves to the newly-updated policy.
 *
 * Each time the policy is updated, `revision` increments by 1. The existing `revision` must be included when updating the policy. This ensures you're working with the latest policy and prevents unintended overwrites.
 */
export function updatePolicy(payload: object): RequestOptionsFactory<any> {
  function __updatePolicy({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFieldMaskToRESTFieldMask,
        paths: [{ path: 'mask' }],
      },
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [{ path: 'policy.createdDate' }, { path: 'policy.updatedDate' }],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.events.v2.policy',
      method: 'PATCH' as any,
      methodFqn: 'wix.events.v2.PolicyManagement.UpdatePolicy',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixEventsV2PolicyManagementUrl({
        protoPath: '/v2/policies/{policy.id}',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'policy.createdDate' },
              { path: 'policy.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __updatePolicy;
}

/**
 * Permanently deletes a policy.
 *
 *
 * <!--
 * > Note: This function is restricted and only runs if you elevate permissions using the [wix-auth.elevate()](https://www.wix.com/velo/reference/wix-auth/elevate) function.
 * -->
 *
 * The `deletePolicy()` function returns a Promise that resolves when the specified policy is deleted.
 *
 * Deleted policies are not returned by the `getPolicy()` or `queryPolicies()` functions.
 */
export function deletePolicy(payload: object): RequestOptionsFactory<any> {
  function __deletePolicy({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.events.v2.policy',
      method: 'DELETE' as any,
      methodFqn: 'wix.events.v2.PolicyManagement.DeletePolicy',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixEventsV2PolicyManagementUrl({
        protoPath: '/v2/policies/{policyId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
    };

    return metadata;
  }

  return __deletePolicy;
}

/**
 * Creates a query to retrieve a list of policies, given the provided paging and filter.
 *
 *
 * The `queryPolicies()` function builds a query to retrieve a list of policies and returns a [PoliciesQueryBuilder](https://www.wix.com/velo/reference/wix-events-v2/policies/policiesquerybuilder) object.
 *
 * The returned object contains the query definition which is typically used to run the query using the [`find()`](https://www.wix.com/velo/reference/wix-events-v2/policies/policiesquerybuilder/find) function.
 *
 * You can refine the query by chaining `PoliciesQueryBuilder` functions onto the query. `PoliciesQueryBuilder` functions enable you to sort, filter and control the results that `PoliciesQueryBuilder.find()` returns.
 *
 * The query runs with the following `PoliciesQueryBuilder` defaults that you can override:
 *
 * [`limit`](https://www.wix.com/velo/reference/wix-events-v2/policies/policiesquerybuilder/limit): `50`
 * [`descending`](https://www.wix.com/velo/reference/wix-events-v2/policies/policiesquerybuilder/descending): `_createdDate`
 *
 * The functions that are chained to `queryPolicies()` are applied in the order they are called. For example, if you sort on the `_createdDate` property in ascending order and then on the id property in descending order, the results are sorted by the created date and then, if there are multiple results with the same date, the items are sorted by the id.
 *
 * The table below shows which `PoliciesQueryBuilder` functions are supported for `queryPoliciesGuests()`. You can only use one filter function for each property. If a property is used in more than one filter, only the first filter will work.
 */
export function queryPolicies(payload: object): RequestOptionsFactory<any> {
  function __queryPolicies({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.events.v2.policy',
      method: 'POST' as any,
      methodFqn: 'wix.events.v2.PolicyManagement.QueryPolicies',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixEventsV2PolicyManagementUrl({
        protoPath: '/v2/policies/query',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'policies.createdDate' },
              { path: 'policies.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __queryPolicies;
}

/**
 * Changes policy order in an event dashboard and agreement checkbox on the checkout form.
 * For example, if we have 3 policies in the list, after using this function the 3rd policy will become the 1st, and other policies will move by 1 position. By default, the policies are arranged by the created date in descending order.
 *
 * >  **Note**: it is possible to use both `beforePolicyId` and `afterPolicyId` at the same time but only the last one defined will be executed.
 *
 * <!--
 * >  Note: This function is restricted and only runs if you elevate permissions using the [wix-auth.elevate()](https://www.wix.com/velo/reference/wix-auth/elevate) function.
 * -->
 *
 * The `reorderEventPolicies()` function returns a Promise that resolves to the newly-reordered policy.
 */
export function reorderEventPolicies(
  payload: object
): RequestOptionsFactory<any> {
  function __reorderEventPolicies({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.events.v2.policy',
      method: 'POST' as any,
      methodFqn: 'wix.events.v2.PolicyManagement.ReorderEventPolicies',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixEventsV2PolicyManagementUrl({
        protoPath: '/v2/policies/reorder',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'policies.createdDate' },
              { path: 'policies.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __reorderEventPolicies;
}

/**
 * Retrieves a policy by ID.
 *
 *
 * The `getPolicy()` function returns a Promise that resolves to a policy whose ID matches the given ID.
 */
export function getPolicy(payload: object): RequestOptionsFactory<any> {
  function __getPolicy({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.events.v2.policy',
      method: 'GET' as any,
      methodFqn: 'wix.events.v2.PolicyManagement.GetPolicy',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixEventsV2PolicyManagementUrl({
        protoPath: '/v2/policies/{policyId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'policy.createdDate' },
              { path: 'policy.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __getPolicy;
}
